import { useUserEventLogger } from '@repo/tracking-service';
import { BoxButton, Description, Divider, List, semanticColor, Svg, Table, TableCell, useDialog } from '@repo/ui';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useRelayEnvironment } from 'react-relay';

import { checkRefund } from '@/components/tax-refund/common/graphql/query/checkRefund';
import ApplyResultFaqView from '@/components/tax-refund/common/view/ApplyResultFaqView';
import ApplyResultInfoView from '@/components/tax-refund/common/view/ApplyResultInfoView';
import { getCorporationDefaultInfo, getIndividualDefaultInfo } from '@/lib/constants/info-contents';
import { COMMON_PATHS, CORPORATION_PATHS, INDIVIDUAL_PATHS } from '@/lib/constants/paths';
import { cancelDataAtom } from '@/lib/stores/cancel-data';
import { formatAccountBankNo } from '@/lib/utils/stringFormat';

export const ApplyResultApplyCompleteCheckContent = ({ data }: { data: any }) => {
  const relayEnvironment = useRelayEnvironment();
  const router = useRouter();
  const { sendClickEvent, sendViewEvent } = useUserEventLogger();
  const { showDialog } = useDialog();
  const setCancelData = useSetAtom(cancelDataAtom);
  const [disabledCancelButton, setDisabledCancelButton] = useState(false);

  const isCorps = data.businessType === 'corps';
  const displayName = isCorps ? (data.fullName ?? data.name) : data.name;
  const { cardLogo, cardName, cardNumber, hasRegisteredCard } = data.paymentCard;

  const faqContents = [
    {
      title: '환급금은 언제 입금되나요?',
      content: '비즈넵 환급 제휴 세무사가 검토 후 경정청구 신고 접수를 완료하면, 신고 접수 이후 2개월 이내에 환급 결과를 확인할 수 있습니다.'
    },
    {
      title: '환급이 불가한 경우에도 이용료가 발생하나요?',
      content:
        '환급금을 받지 못할 때는 이용료가 발생하지 않습니다. 이용료는 제휴 세무사 비용으로 결정 환급액의 30%(부가세 별도)이기 때문에 환급액이 감소하면 제휴 세무사 비용도 감소합니다.'
    },
    {
      title: '이용료는 어떤 기준으로 책정되는 건가요?',
      content:
        '비즈넵 환급 서비스 이용 시 발생하는 이용료는 비즈넵 환급 제휴 세무사의 신고대리에 따른 비용으로 업계 평균 이용료로 책정되었습니다. 비즈넵 환급을 이용하시면 업계 평균 이용료로 추가 서류 제출 없이 쉽고 간편하게 환급받으실 수 있습니다.'
    },
    {
      title: '환급 신청은 했는데 전화가 안 와요.',
      content: '비즈넵 환급은 신청 후 모든 진행 과정을 카카오톡 알림톡을 통해 안내드리고 있습니다. 신청 후 카카오톡을 확인해주세요.'
    },
    {
      title: '환급금을 어떻게 찾았나요?',
      content: isCorps
        ? '비즈넵 환급은 과거 5년 동안 사업소득에 대한 법인세를 신고하며 놓쳤던 세액 공제, 세액 감면 항목들을 고도화된 AI 기술로 찾아냅니다. 그리고 비즈넵 환급과 제휴한 세무 전문가가 탐색 결과값을 검토하여 놓치는 부분 없이 환급금을 찾아드리고 있습니다.'
        : '비즈넵 환급은 과거 5년간 개인사업자의 종합소득세 등을 신고하며 놓쳤던 세액 공제, 세액 감면 항목들을 고도화된 AI 기술로 찾아냅니다. 그리고 비즈넵 환급과 제휴한 세무 전문가가 탐색 결과값을 검토하여 놓치는 부분 없이 환급금을 찾아드리고 있습니다.'
    },
    {
      title: '몇 년 치 환급금인가요?',
      content: '비즈넵 환급은 과거 5년 동안의 환급금을 찾아드리고 있습니다.'
    },
    {
      title: '제출할 자료가 있나요?',
      content:
        '비즈넵 환급은 서류 제출 없이 쉽고 간편하게 환급금을 찾아드리는 서비스를 제공합니다.\n간혹 국세청에서 검토 중 추가 확인이 필요할 경우에만 서류를 요청드리고 있습니다. 필요한 자료는 제휴 세무 전문가가 안내드릴 예정입니다.'
    }
  ];
  const infoContents = isCorps ? getCorporationDefaultInfo() : getIndividualDefaultInfo(hasRegisteredCard);

  const cancelClickEvent = `${isCorps ? 'corps' : 'indis'}-result_cancel_apply-complete`;
  const refreshDialogEvent = `${isCorps ? 'corps' : 'indis'}-result_cancel_refresh-dialog`;
  const refreshDialogOkEvent = `${refreshDialogEvent}-ok`;

  const handleCardClick = () => {
    if (isCorps) {
      return;
    }

    router.push(COMMON_PATHS.환급결제카드입력);
  };

  const handleClickCancel = async () => {
    if (router.pathname.includes('reenactment')) return;

    sendClickEvent(cancelClickEvent);
    setDisabledCancelButton(true);

    const refund = await checkRefund(relayEnvironment, data.refundId);
    const isRefundCancelable = refund?.cancelStage !== 'Canceling' && refund?.refundStatus === 'ApplyComplete' && refund?.refundStage === 'Review';

    if (isRefundCancelable) {
      setCancelData({
        refundId: data.refundId,
        name: displayName,
        businessType: data.businessType
      });
      router.push({
        pathname: isCorps ? CORPORATION_PATHS.법인취소방어 : INDIVIDUAL_PATHS.개인취소방어,
        query: router.query
      });
      setDisabledCancelButton(false);
      return;
    }

    sendViewEvent(refreshDialogEvent, undefined, { isAllowRetry: true });
    showDialog({
      title: '진행 단계에 변동이 있어 새로고침이 필요해요',
      description: '확인을 누르시면 현재 상태로 페이지가 새로고침돼요.',
      buttons: {
        primary: {
          onClick: () => {
            sendClickEvent(refreshDialogOkEvent);
            setDisabledCancelButton(false);
            router.reload();
          }
        }
      }
    });
  };

  useEffect(() => {
    setCancelData(undefined);
  }, [setCancelData]);

  return (
    <div>
      <div className={'px-4.5 py-normal-small'}>
        <Description
          title={
            isCorps ? `제휴 세무사가 검토한 후 ’${displayName}’의 환급 신고가 진행돼요` : `제휴 세무사가 검토한 후\n${displayName}님의 환급 신고가 진행돼요`
          }
          size={'medium'}
          description={'카카오 알림톡으로 진행 과정을 안내드릴게요.'}
          graphic={<Svg src="icg_phone-kakao_md.svg" width={80} height={80} />}
        />
      </div>
      <Divider size={'large'} />
      <div className={'px-4.5 py-tiny-large'}>
        <div className={'pb-normal-large'}>
          <div className={'body-large-regular text-neutral-foreground-low pt-normal-large'}>{isCorps ? '법인세 예상 환급액' : '종합소득세 예상 환급액'}</div>
          <div className={'text-neutral-foreground-main heading-medium-bold'}>{data.refundAmount}</div>
          <Divider className={'my-normal-medium'} />
          <Table
            headerCell={<TableCell variant={'header'}>환급 신청일</TableCell>}
            contentCell={<TableCell className={'flex-1 text-right'}>{data.applyDate}</TableCell>}
          />
          <Table
            headerCell={<TableCell variant={'header'}>예상 환급일</TableCell>}
            contentCell={<TableCell className={'flex-1 text-right'}>신청일부터 2~3개월</TableCell>}
          />
          {data.applicationInfo && (
            <Table
              headerCell={<TableCell variant={'header'}>환급 계좌</TableCell>}
              contentCell={
                <TableCell className={'flex-1 text-right'}>{formatAccountBankNo(data.applicationInfo?.bank, data.applicationInfo?.accountNo)}</TableCell>
              }
            />
          )}
          <Table
            headerCell={<TableCell variant={'header'}>이용료</TableCell>}
            contentCell={
              <TableCell className={'flex-1 text-right'}>
                <div>
                  <p className={'mb-1'}>결정 환급액의 {data.defaultFeeRate}%</p>
                  <p className={'text-13 text-gray-500'}>(부가세 별도)</p>
                </div>
              </TableCell>
            }
          />
          {+data.discountRate !== 0 && (
            <Table
              headerCell={<TableCell variant={'header'}>이용료 할인</TableCell>}
              contentCell={
                <TableCell className={'flex-1 text-right'}>
                  <div>
                    <p className={'mb-1'}>제휴 할인 {data.discountRate}% 적용</p>
                    <p className={'text-13 text-gray-500'}>
                      결정 환급액의 <span className={'line-through'}>{data.defaultFeeRate}%</span>-&gt;{data.feeRate}% (vat별도)
                    </p>
                  </div>
                </TableCell>
              }
            />
          )}
          {!isCorps && hasRegisteredCard && cardNumber && (
            <Table
              headerCell={<TableCell variant={'header'}>결제 카드</TableCell>}
              contentCell={
                <>
                  <TableCell className={'flex items-center flex-1 justify-end'}>
                    <div className={'flex gap-tiny-medium text-[15px]'}>
                      <Svg src={cardLogo} width={24} height={24} />
                      {cardName}({cardNumber.slice(-4)})
                    </div>
                  </TableCell>
                  <TableCell>
                    <BoxButton size={'small'} variant={'tonal-secondary'} onClick={handleCardClick}>
                      변경
                    </BoxButton>
                  </TableCell>
                </>
              }
            />
          )}
        </div>
        <ApplyResultInfoView data={infoContents} />
        <Divider />
        <div className={'pt-tiny-large'}>
          <List
            title={''}
            primaryAction
            onClick={handleClickCancel}
            state={disabledCancelButton ? 'disabled' : 'enabled'}
            supportingElement={
              <div className={`flex items-center text-primary-foreground-main body-small-medium ${disabledCancelButton && 'opacity-40'}`}>
                신청 취소
                <Svg src={'ic_chevron-right.svg'} width={18} height={18} color={semanticColor.primary.foreground.main} />
              </div>
            }
          />
        </div>
      </div>
      <Divider size={'large'} />
      <ApplyResultFaqView data={faqContents} clickEventName={`${isCorps ? 'corps' : 'indis'}-result_faq_apply-complete`} />
      <div className={'pb-normal-medium'} />
    </div>
  );
};
